/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { KeyboardEvent, MutableRefObject, SyntheticEvent, useRef } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import classNames from 'classnames';

import * as K from 'common/constants';
import log from 'common/ScalyrLogger';
import { LogEntry } from 'common/graphqlTypes';
import { useOutsideEvent } from 'hooks/useOutsideEvent';
import { PARSE_TYPE, TOKEN_TYPE } from 'common/util/filter/types';
import { unquote } from 'common/util/string';
import { copyToClipboard, getSelectedRange, getSelectedText, selectElementContents } from 'service/browser';

import { selectedTokenState } from 'components/common/Managers/RouteStateManager.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import {
  LoglineSelectionState,
  loglineSelectionState,
  loglineSelectionStateDefault,
  virtualReferenceState,
} from 'components/search/SearchLogsPage/LoglineSelectionPopover';

import InspectFieldsDrawer from 'components/search/SearchLogsPage/InspectFieldsDrawer';
import { logResultsScrollOffset, logToInspectState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { useLogInspectorState } from 'hooks/useLogInspectorState';

import { pinLogDetailsState } from 'components/search/SearchLogsPage/SearchStateManager.state';

// time in ms to see if a click turns into a double-click
const DBL_CLICK_WAIT = 200;

type EventsContainerProps = {
  eventsView: JSX.Element;
  getEventByToken: (token: string) => LogEntry | null;
  getTokenByOffset: (token: string, offset: number) => string;
  outerClassName?: string;
  innerClassName?: string;
  outsideRefs?: Array<MutableRefObject<HTMLDivElement | null>>;
  onClick?: (token: string) => void;
  displayMode?: K.DISPLAY_MODE;
};

/**
 * This component is a container that adds a bunch of behaviors to a list of log lines:
 *
 *   - show LogDetails on click, hide on Esc or outside click
 *   - show next/previous event in LogDetails by using up/down arrow
 *   - handle text selection, show popover
 *   - enhanced copy functionality (clean up copied content)
 *
 * The parent component provides the actual view of the results, which is wrapped in two elements (in order to handle
 * stuff like overflow and scrolling). This component does not deal directly with the event data.
 *
 * @prop eventsView          a component that shows search results
 * @prop getEventByToken     a function that returns a logEntry associated with the given continuation token
 * @prop getTokenByOffset    a function that returns a continuation token relative to another one in the results
 * @prop outerClassName      class name(s) for the outer DIV
 * @prop innerClassName      class name(s) for the inner DIV
 * @prop outsideRefs         refs to components not considered "outside"
 * @prop onClick             function to run when click happens; it will be passed token for the clicked log line
 * @prop displayMode         display mode, if possibly different from settings
 */
function EventsContainer({
  eventsView,
  getEventByToken,
  getTokenByOffset,
  outerClassName = '',
  innerClassName = '',
  outsideRefs = [],
  onClick,
  displayMode,
}: EventsContainerProps): JSX.Element {
  const settings = useRecoilValue(settingsState);
  const curDisplayMode = displayMode || settings[K.SETTING.DISPLAY_MODE];
  const [selectedToken, setSelectedToken] = useRecoilState(selectedTokenState);
  const setLoglineSelection = useSetRecoilState(loglineSelectionState);
  const setVirtualReference = useSetRecoilState(virtualReferenceState);
  const { isLogInspectorPreventedFromClosing, allowLogInspectorToClose } = useLogInspectorState();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const clickTimer = useRef(0);

  const isPinLogDetails = useRecoilValue(pinLogDetailsState);
  const [logToInspect, setLogToInspect] = useRecoilState(logToInspectState);

  // Callback used for arrow key navigation from logline to logline.
  const navigateToToken = useRecoilCallback(({ set, snapshot }) => (increment: number) => {
    const token = snapshot.getLoadable(selectedTokenState).contents as string;
    const newToken = getTokenByOffset(token, increment);
    if (newToken) {
      set(selectedTokenState, newToken);
      set(logResultsScrollOffset, { cursor: newToken });
    }
  });

  const showDrawer = (token: string): void => {
    setLoglineSelection(loglineSelectionStateDefault);
    setSelectedToken(token);
  };

  const hideDrawer = (): void => {
    setSelectedToken('');
    setLogToInspect(null);
    allowLogInspectorToClose();
  };

  const handleOutsideMousedown = (): void => {
    if (selectedToken && !isPinLogDetails && !isLogInspectorPreventedFromClosing) {
      hideDrawer();
    }
  };
  useOutsideEvent('mousedown', outsideRefs.concat(containerRef), handleOutsideMousedown);

  /**
   * Sets up LoglineSelectionPopover with info about what was selected.
   *
   * @param target    the element that got the click event
   * @return true if text was selected
   */
  const handleTextSelection = (target: HTMLElement): boolean => {
    const range = getSelectedRange();
    const selectedText = unquote(getSelectedText()).trim();

    if (!range || !selectedText) {
      return false;
    }

    // We source the context from `data-*` attributes and the event cache
    const termEl = target.closest('[data-token-type="term"]');
    const hasMultipleTerms = (termEl?.querySelectorAll<HTMLSpanElement>('[data-term-field')?.length ?? 0) > 1;
    const fieldEl = hasMultipleTerms
      ? target.closest<HTMLSpanElement>('[data-term-field]')
      : termEl?.querySelector<HTMLSpanElement>('[data-term-field]') || null;
    const field = fieldEl?.dataset.termField || '';
    const valueEl = hasMultipleTerms
      ? target.closest<HTMLSpanElement>('[data-term-value-type]')
      : termEl?.querySelector<HTMLSpanElement>('[data-term-value-type]') || null;

    const eventEl = target.closest('[data-continuation-token]') as HTMLElement;
    const token = eventEl?.dataset.continuationToken;
    const logEntry = getEventByToken(token || '');

    const selectedTermValue = unquote(logEntry?.values[field] || '');
    const targetText = unquote((target.innerText || '').trim());
    const partial = selectedText !== targetText;

    const selectionOptions: LoglineSelectionState = {
      text: partial ? selectedText : String(selectedTermValue) || selectedText,
      type: (valueEl?.dataset.termValueType as TOKEN_TYPE) || TOKEN_TYPE.UNKNOWN,
      field,
      partial,
    };

    setLoglineSelection(selectionOptions);
    setVirtualReference({
      // virtual element reference used by PopperJS to position LoglineSelectionPopover {@see https://popper.js.org/react-popper/v2/virtual-elements/}
      getBoundingClientRect(): ClientRect {
        return range.getBoundingClientRect();
      },
    });

    return true;
  };

  // use a delegated onClick handler in the container DIV rather than assigning handlers to each log line
  const handleClick = (event: SyntheticEvent) => {
    // TODO: LogResultsContainer no longer renders on every click, but LogResults does {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-3798}
    const target = event.target as HTMLDivElement;

    // user is trying to see full value of a truncated facet value in a dialog - don't show the Inspect Fields drawer
    if (target.dataset.isMoreLink) {
      return;
    }

    const line: HTMLDivElement | null = target.closest('[data-continuation-token]');
    const token = line?.dataset?.continuationToken;

    if (clickTimer.current) {
      window.clearTimeout(clickTimer.current);
    }

    // A log line that hasn't loaded its data has an empty string as data-continuation-token. Return early.
    if (!token) {
      hideDrawer();
      if (onClick) {
        onClick('');
      }
      return;
    }

    log.debug('line clicked for token: ' + token);

    const timerId = window.setTimeout(() => {
      if (handleTextSelection(target)) {
        return;
      }
      if (onClick) {
        onClick(token);
      }
      if (token === selectedToken) {
        hideDrawer();
      } else {
        showDrawer(token);
      }
    }, DBL_CLICK_WAIT);

    clickTimer.current = timerId;
  };

  // 1. Clear single-click timer so we don't show drawer on dbl-click
  // 2. If user double-clicks within a term, select the whole term
  const onDoubleClick = (event: SyntheticEvent) => {
    if (clickTimer.current) {
      window.clearTimeout(clickTimer.current);
    }

    const target = event.target as HTMLElement;
    const parent: HTMLElement | null = target.parentElement;
    if (parent) {
      // Note: this depends on the DOM structure of LogLine
      if (parent.dataset.tokenType === PARSE_TYPE.TERM) {
        selectElementContents(parent);
      }
      handleTextSelection(parent);
    }
  };

  // Callback function that is fired when the user scrolls in the logResults area.
  // TODO: this doesn't work because the scroll handler is one DIV too high
  const handleScroll = () => {
    // Clear the selection text state so the popover will hide on scroll.
    setLoglineSelection(loglineSelectionStateDefault);
  };

  // If inspect fields drawer is open, allow the user to navigate loglines using the arrow keys or to close the drawer
  // with the escape key.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const key = event.key as K.KEYBOARD_KEY;
    const isHandledKey = [K.KEYBOARD_KEY.ARROW_DOWN, K.KEYBOARD_KEY.ARROW_UP, K.KEYBOARD_KEY.ESCAPE].includes(key);
    if (selectedToken && isHandledKey) {
      event.preventDefault();
      event.stopPropagation();
      setLogToInspect(null);
      if (key === K.KEYBOARD_KEY.ARROW_UP) {
        navigateToToken(-1);
      } else if (key === K.KEYBOARD_KEY.ARROW_DOWN) {
        navigateToToken(1);
      } else if (key === K.KEYBOARD_KEY.ESCAPE) {
        hideDrawer();
      }
    }
    // Copy/paste functionality is blocked in table and wrapped line mode.
    // This is a temporary hack to allow for copying to clipboard using the keyboard shortcut CMD+C/ CTRL+C
    // Right click, selecting 'copy' still broken.
    // TODO: find out what is not playing nicely with the react-base-table library and is causing copy/paste to be disabled
    // {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-6719}
    if (curDisplayMode !== K.DISPLAY_MODE.LINES && event.keyCode === K.KEY_CODE.C && (event.ctrlKey || event.metaKey)) {
      const selectedText = unquote(getSelectedText()).trim();
      copyToClipboard(selectedText);
    }
  };

  /**
   * In LINES mode, modify text copied to clipboard by replacing the zero-width space character
   * {@link https://en.wikipedia.org/wiki/Zero-width_space} with a single space.
   *
   * This requires permission from the user, and browser support for navigator.clipboard.readText. If either
   * of those conditions is not met, or if the user is not in LINES mode, this function is a no-op.
   *
   * TODO: provide a more comprehensive solution {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-7140}
   **/
  const handleCopy = (): void => {
    if (!navigator?.clipboard?.readText || curDisplayMode !== K.DISPLAY_MODE.LINES) {
      return;
    }
    navigator.clipboard.readText().then((selectedText) => {
      if (selectedText) {
        copyToClipboard(selectedText.replace(/\u200B/g, ' '));
      }
    });
  };

  const isLogLineSelected = !!getEventByToken(selectedToken);
  const isPinnedLogDetails = useRecoilValue(pinLogDetailsState);
  const resizeLogResults = isLogLineSelected && !isPinnedLogDetails ? 'LogResultsWithOverlay' : '';
  let enableScrollOnResults = '';
  // Check to add overflow styling to wrapped log results container when displaying overlay
  // and to table results container when pinned is open
  if (
    (isLogLineSelected && curDisplayMode === K.DISPLAY_MODE.WRAPPED && !isPinnedLogDetails) ||
    (curDisplayMode === K.DISPLAY_MODE.TABLE && isPinnedLogDetails)
  ) {
    enableScrollOnResults = 'LogResultsScroll';
  }

  return (
    <div
      className={classNames(outerClassName, resizeLogResults, enableScrollOnResults)}
      onCopy={handleCopy}
      onKeyDown={handleKeyDown}
      ref={containerRef}
      role="main"
      tabIndex={0}
    >
      <div
        className={innerClassName}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        onScroll={handleScroll}
        role="list"
        tabIndex={-1}
      >
        {eventsView}
      </div>
      {(isLogLineSelected || logToInspect) && (
        <InspectFieldsDrawer
          open={isLogLineSelected}
          close={(): void => {
            hideDrawer();
          }}
          logEntry={logToInspect}
        />
      )}
    </div>
  );
}

export default EventsContainer;
